<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl">Invoices</h1>
    <Button variant="outline" @click="exportToCSV" :disabled="allInvoices.length === 0" title="Download CSV">
      <Download class="h-4 w-4" />
      Download
    </Button>
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <InvoicesControls
      @update:status="onStatusChange"
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="invoicesTableColumns"
      :data="paginatedInvoices"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-number="pageNumber"
      :page-size="pageSize"
      @update:page="onUpdatePage"
    />
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import type { InvoiceParams } from '@/models/invoices';
import { fetchInvoices } from '@/services/invoices';
import { LAYUP_API_GET_ITEMS_LIMIT } from '@/services/layupApi';
import { currency, displayDate } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import { Download } from 'lucide-vue-next';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref } from 'vue';
import type { ModelInvoice } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import InvoicesControls from '../invoices-controls/InvoicesControls.vue';
import { invoicesTableColumns } from './InvoicesTableColumns';

const isLoading = ref(true);
const invoiceParams = ref<InvoiceParams>({ limit: LAYUP_API_GET_ITEMS_LIMIT });
const previousParams = ref<InvoiceParams>({ ...invoiceParams.value });
const allInvoices = ref<Partial<ModelInvoice>[]>([]);

const paginatedInvoices = computed(() => {
  return allInvoices.value.slice((pageNumber.value - 1) * pageSize.value, pageNumber.value * pageSize.value);
});

const totalPages = ref(1);
const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);

onMounted(async () => onUpdateFilter(invoiceParams.value, true));

const onUpdateFilter = async (params: InvoiceParams, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  allInvoices.value = [];
  isLoading.value = true;

  try {
    const data = await fetchInvoices(params);

    if (data?.invoices && Object.keys(data.invoices).length > 0) {
      data.invoices.forEach((invoice: ModelInvoice) => {
        const invoiceInfo: Partial<ModelInvoice> = {
          _id: invoice._id,
          createdAt: displayDate(invoice.createdAt),
          accountingInvoiceNumber: invoice.accountingInvoiceNumber || 'Not available',
          totalAmount: invoice.totalAmount,
          status: invoice.status,
          reference: invoice.reference,
        };
        allInvoices.value.push(invoiceInfo);
      });

      totalPages.value = Math.ceil(allInvoices.value.length / pageSize.value);
      totalCount.value = allInvoices.value.length;
      pageNumber.value = 1;
    }
    addMetric('invoices-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoices-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('invoices-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onStatusChange = (value: string): void => {
  invoiceParams.value.status = value;
  onUpdateFilter(invoiceParams.value);
};

const onDateRangeChange = (value: DateRange): void => {
  invoiceParams.value.from = new Date(`${value.start?.year}-${value.start?.month}-${value.start?.day}`);
  invoiceParams.value.to = new Date(`${value.end?.year}-${value.end?.month}-${value.end?.day}`);
  onUpdateFilter(invoiceParams.value);
};

const onClearFilters = (): void => {
  invoiceParams.value = { limit: LAYUP_API_GET_ITEMS_LIMIT };
  onUpdateFilter(invoiceParams.value);
};

const onSearchTermChange = (value: string): void => {
  invoiceParams.value.reference = value.toUpperCase();
  onUpdateFilter(invoiceParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
};

const exportToCSV = () => {
  if (allInvoices.value.length === 0) {
    console.warn('No data available for export');
    return;
  }

  const csvContent = generateCSV(allInvoices.value);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `invoices-${dayjs().format('YYYY-MM-DD')}.csv`);
  link.click();

  URL.revokeObjectURL(url);
};

const generateCSV = (data: Partial<ModelInvoice>[]): string => {
  const headerMap: Partial<Record<keyof ModelInvoice, string>> = {
    createdAt: 'Date Created',
    accountingInvoiceNumber: 'Invoice Number',
    totalAmount: 'Total Amount',
    reference: 'Reference',
    status: 'Status',
  };

  const headers = Object.keys(headerMap) as Array<keyof ModelInvoice>;
  const headerTitles = headers.map((header) => headerMap[header] || header);

  const rows = data.map((row) =>
    headers
      .map((header) => {
        let value = row[header] || '';
        if (header === 'totalAmount') value = currency(value);
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(','),
  );

  return [headerTitles.join(','), ...rows].join('\n');
};
</script>
